import { computed, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

export type Theme = 'light' | 'dark';

export const THEME_COOKIE = 'brain-theme';

const TRANSITION_MS = 250;

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly cookieService = inject(CookieService);
  private readonly document = inject(DOCUMENT);

  private readonly _theme = signal<Theme>(this.read());

  private transitionTimer?: ReturnType<typeof setTimeout>;

  readonly theme = this._theme.asReadonly();
  readonly isDark = computed(() => this._theme() === 'dark');

  constructor() {
    this.apply(this._theme());
  }

  toggle(): void {
    this.set(this._theme() === 'dark' ? 'light' : 'dark');
  }

  set(theme: Theme): void {
    this._theme.set(theme);
    this.apply(theme);
    this.cookieService.set(THEME_COOKIE, theme, 365, '/');
  }

  private read(): Theme {
    return this.cookieService.get(THEME_COOKIE) === 'dark' ? 'dark' : 'light';
  }

  private apply(theme: Theme): void {
    const root = this.document.documentElement;

    root.classList.add('theme-transitioning');
    root.setAttribute('data-theme', theme);

    clearTimeout(this.transitionTimer);
    this.transitionTimer = setTimeout(() => root.classList.remove('theme-transitioning'), TRANSITION_MS);
  }
}
